import React, { useState, useEffect } from "react";
import Banner from "./Banner/Banner";
import {
  mentorsFaq,
  authorFaq,
  institutionsFaq,
  cacFaq,
  generalFaqPublishing,
  generalFaqEvents,
  generalFaqCertificates,
} from "./FAQData";
import styles from "./FAQ.module.css";

const FAQPage = () => {
  // independent states for each FAQ section
  const [openMentor, setOpenMentor] = useState(null);
  const [openAuthor, setOpenAuthor] = useState(null);
  const [openInstitution, setOpenInstitution] = useState(null);
  const [openCAC, setOpenCAC] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [faqData, setFaqData] = useState(null);

  const handleSearch = (query) => {
    setSearchQuery(query);
    setOpenMentor(null);
    setOpenAuthor(null);
    setOpenInstitution(null);
    setOpenCAC(null);
  };

  // Highlight search matches
  const highlightText = (text) => {
    if (!searchQuery) return text;
    const regex = new RegExp(`(${searchQuery})`, "gi");
    return text
      .split(regex)
      .map((part, idx) =>
        regex.test(part) ? <mark key={idx}>{part}</mark> : part
      );
  };


  // Fetch FAQ data from API
  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        setLoading(true);
        const baseUrl = process.env.REACT_APP_API_BASE_URL || "https://api.imparc.site/";
        const response = await fetch(`${baseUrl}api/faq`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const apiResponse = await response.json();

        // Extract FAQs data from the nested response structure
        if (apiResponse.success && apiResponse.faqs && apiResponse.faqs) {
          // Group FAQs by category
          const groupedFAQs = apiResponse.faqs.reduce((acc, faq) => {
            const category = faq.category || 'general';
            if (!acc[category]) {
              acc[category] = [];
            }
            acc[category].push({
              id: faq.id,
              question: faq.title,
              answer: faq.description,
              category: faq.category
            });
            return acc;
          }, {});

          setFaqData(groupedFAQs);
        } else {
          throw new Error('Invalid API response structure');
        }

        setError(null);
      } catch (err) {
        setError('Failed to load FAQs. Please try again later.');
        console.error('Error fetching FAQs:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFAQs();
  }, []);

  return (
    <main className={styles.faqPage}>
      <Banner placeholder="Search FAQs..." onSearch={handleSearch} />

      {/* Mentors FAQ */}
      <div className={styles.section}>
        <div className={styles.container}>
          <h3>
            Mentors <span>FAQ</span>
          </h3>
          <h5>
            Common questions about becoming a mentor and mentorship
            responsibilities
          </h5>
          {faqData?.mentor?.map((item, idx) => (
            <div
              key={item.id}
              className={`${styles.faqItem} ${openMentor === idx ? styles.active : ""
                }`}
              onClick={() => setOpenMentor(openMentor === idx ? null : idx)}
            >
              <div className={styles.question}>{highlightText(item.question)}</div>
              {openMentor === idx && (
                <div className={styles.answer}>{highlightText(item.answer)}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Author Members FAQ */}
      <div className={styles.authorMemberBG}>
        <div className={styles.container}>
          <h3>
            Author <span>Members FAQ</span>
          </h3>
          <h5>
            Questions about author membership benefits and publication
            opportunities
          </h5>
          <div className={styles.faqOuter}>
            {faqData?.author?.map((item, idx) => (
              <div
                key={item.id}
                className={`${styles.faqItem} ${openAuthor === idx ? styles.active : ""
                  }`}
                onClick={() => setOpenAuthor(openAuthor === idx ? null : idx)}
              >
                <div className={styles.question}>{highlightText(item.question)}</div>
                {openAuthor === idx && (
                  <div className={styles.answer}>{highlightText(item.answer)}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Institutions FAQ */}
      <div className={styles.section}>
        <div className={styles.container}>
          <h3>
            Institutions <span> FAQ</span>
          </h3>
          <h5>
            Information about knowledge Hubs, CAC partnerships, and institutional collaboration          </h5>
          <h2 className={styles.sectionTitle}>Knowledge Hub FAQs</h2>
          {faqData?.knowledge_hub?.map((item, idx) => (
            <div
              key={item.id}
              className={`${styles.faqItem} ${openInstitution === idx ? styles.active : ""
                }`}
              onClick={() =>
                setOpenInstitution(openInstitution === idx ? null : idx)
              }
            >
              <div className={styles.question}>{highlightText(item.question)}</div>
              {openInstitution === idx && (
                <div className={styles.answer}>{highlightText(item.answer)}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* CAC FAQ */}
      <div className={`${styles.section} ${styles.borderTop}`}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>CAC Partnership FAQs</h2>
          {faqData?.cac_partnership?.map((item, idx) => (
            <div
              key={item.id}
              className={`${styles.faqItem} ${openCAC === idx ? styles.active : ""
                }`}
              onClick={() => setOpenCAC(openCAC === idx ? null : idx)}
            >
              <div className={styles.question}>{highlightText(item.question)}</div>
              {openCAC === idx && (
                <div className={styles.answer}>{highlightText(item.answer)}</div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* General FAQ */}
      <div className={styles.generalFaqBG}>
        <div className={styles.container}>
          <h3>
            General <span>FAQ</span>
          </h3>
          <h5>
            Publishing, events, indexing, certificates, and general inquiries
          </h5>
          <div className={styles.columOuter}>
            <div className={styles.colum}>
              <h6>Publishing</h6>
              {faqData?.general_publish?.slice(0, 4).map((item, idx) => (
                <div key={item.id} className={styles.faqItemBlock}>
                  <div className={styles.question}>{highlightText(item.question)}</div>
                  <div className={styles.answer}>{highlightText(item.answer)}</div>
                </div>
              ))}
            </div>
            <div className={styles.colum}>
              <h6 className={styles.icon2}>Events & Indexing</h6>
              {faqData?.general_event?.slice(0, 4).map((item, idx) => (
                <div key={item.id} className={styles.faqItemBlock}>
                  <div className={styles.question}>{highlightText(item.question)}</div>
                  <div className={styles.answer}>{highlightText(item.answer)}</div>
                </div>
              ))}
            </div>
            <div className={styles.colum}>
              <h6 className={styles.icon3}>Certificates & General</h6>
              {faqData?.general_certificate?.slice(0, 4).map((item, idx) => (
                <div key={item.id} className={styles.faqItemBlock}>
                  <div className={styles.question}>{highlightText(item.question)}</div>
                  <div className={styles.answer}>{highlightText(item.answer)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default FAQPage;
